/**
 * useDatasets hook - Dataset list management with filtering, sorting, pagination
 * @module hooks/starnexus/useDatasets
 *
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  Dataset,
  DatasetStatus,
  ProcessingType
} from '../../types/starnexus';
import {
  datasetService,
  type DatasetFilters,
  type DatasetSortConfig
} from '../../services/starnexus';

export interface PropertyFilterToken {
  propertyKey: string;
  operator: string;
  value: string;
}

export interface UseDatasetsOptions {
  initialPageSize?: number;
}

export interface UseDatasetsReturn {
  // Data
  datasets: Dataset[];
  totalCount: number;
  loading: boolean;
  error: string | null;

  // Pagination
  currentPage: number;
  pageSize: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // Filtering
  filterText: string;
  setFilterText: (text: string) => void;
  propertyFilters: PropertyFilterToken[];
  setPropertyFilters: (tokens: PropertyFilterToken[]) => void;

  // Sorting
  sortingColumn: DatasetSortConfig | null;
  setSortingColumn: (config: DatasetSortConfig | null) => void;

  // Selection
  selectedItems: Dataset[];
  setSelectedItems: (items: Dataset[]) => void;

  // Actions
  refresh: () => Promise<void>;
  bulkUpdateStatus: (
    ids: string[],
    status: DatasetStatus
  ) => Promise<{ success: string[]; failed: string[] }>;
  bulkDelete: (
    ids: string[]
  ) => Promise<{ success: string[]; failed: string[] }>;
}

export const useDatasets = (
  options: UseDatasetsOptions = {}
): UseDatasetsReturn => {
  const { initialPageSize = 10 } = options;

  // State
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Filter state
  const [filterText, setFilterText] = useState('');
  const [propertyFilters, setPropertyFilters] = useState<PropertyFilterToken[]>(
    []
  );

  // Sort state
  const [sortingColumn, setSortingColumn] = useState<DatasetSortConfig | null>(
    null
  );

  // Selection state
  const [selectedItems, setSelectedItems] = useState<Dataset[]>([]);

  // Convert property filters to service format
  const serviceFilters = useMemo((): DatasetFilters => {
    const filters: DatasetFilters = {};

    if (filterText) {
      filters.search = filterText;
    }

    for (const token of propertyFilters) {
      switch (token.propertyKey) {
        case 'teamName':
          filters.teamId = token.value;
          break;
        case 'status':
          filters.status = token.value as DatasetStatus;
          break;
        case 'processingType':
          filters.processingType = token.value as ProcessingType;
          break;
        case 'successRate':
          if (token.operator === '>=') {
            filters.minSuccessRate = parseFloat(token.value);
          }
          break;
      }
    }

    return filters;
  }, [filterText, propertyFilters]);

  // Fetch datasets
  const fetchDatasets = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await datasetService.listDatasets(
        serviceFilters,
        sortingColumn || undefined,
        currentPage,
        pageSize
      );
      setDatasets(response.items);
      setTotalCount(response.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch datasets');
      setDatasets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [serviceFilters, sortingColumn, currentPage, pageSize]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchDatasets();
  }, [fetchDatasets]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterText, propertyFilters, pageSize]);

  // Clear selection when data changes
  useEffect(() => {
    setSelectedItems([]);
  }, [datasets]);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Bulk operations
  const bulkUpdateStatus = useCallback(
    async (ids: string[], status: DatasetStatus) => {
      const result = await datasetService.bulkUpdateStatus(ids, status);
      await fetchDatasets();
      return result;
    },
    [fetchDatasets]
  );

  const bulkDelete = useCallback(
    async (ids: string[]) => {
      const result = await datasetService.bulkDelete(ids);
      await fetchDatasets();
      return result;
    },
    [fetchDatasets]
  );

  return {
    datasets,
    totalCount,
    loading,
    error,
    currentPage,
    pageSize,
    totalPages,
    setCurrentPage,
    setPageSize,
    filterText,
    setFilterText,
    propertyFilters,
    setPropertyFilters,
    sortingColumn,
    setSortingColumn,
    selectedItems,
    setSelectedItems,
    refresh: fetchDatasets,
    bulkUpdateStatus,
    bulkDelete
  };
};
